import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useChit } from "@/context/ChitContext";
import { useAuth } from "@/context/AuthContext";
import { AddExpenseDrawer } from "@/components/AddExpenseDrawer";
import { calculeazaSolduri, calculeazaDecontari, formateazaRON, getSplits } from "@/lib/settlement";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { GroupHeader } from "@/components/group-detail/GroupHeader";
import { GroupSummaryCard } from "@/components/group-detail/GroupSummaryCard";
import { ClaimPrompt } from "@/components/group-detail/ClaimPrompt";
import { NudgeBanner } from "@/components/group-detail/NudgeBanner";
import { ExpensesTab } from "@/components/group-detail/ExpensesTab";
import { BalancesTab } from "@/components/group-detail/BalancesTab";
import { SettlementTab } from "@/components/group-detail/SettlementTab";
import { AddMemberDialog } from "@/components/group-detail/AddMemberDialog";
import { RenameGroupDialog } from "@/components/group-detail/RenameGroupDialog";
import { LeaveGroupDialog } from "@/components/group-detail/LeaveGroupDialog";

// Reamintire blândă: la câte zile de vechime a datoriei apare bannerul de nudge
// (și, pentru membrii cu cont legat, se trimite automat un email — vezi backend).
const NUDGE_DAYS = 15;

export default function GroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const {
    getGroup,
    addMember,
    deleteExpense,
    addPayment,
    deletePayment,
    joinGroup,
    renameGroup,
    leaveGroup,
  } = useChit();
  const { user, isPremium } = useAuth();

  const [expenseDrawer, setExpenseDrawer] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [memberDialog, setMemberDialog] = useState(false);
  const [newMemberName, setNewMemberName] = useState("");
  const [renameDialog, setRenameDialog] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [leaveConfirm, setLeaveConfirm] = useState(false);

  const group = getGroup(groupId);

  // Dacă grupul nu există (ex: a fost șters), ne întoarcem acasă.
  if (!group) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">Grupul nu a fost găsit.</p>
        <Button onClick={() => navigate("/")} className="mt-4 rounded-xl">
          Înapoi acasă
        </Button>
      </div>
    );
  }

  const numeMembru = (id) => group.members.find((m) => m.id === id)?.name || "?";
  const total = group.expenses.reduce((s, e) => s + e.amount, 0);
  const payments = group.payments || [];

  // Calculăm soldurile (ținând cont și de plățile de decontare) și transferurile.
  const solduri = calculeazaSolduri(group.members, group.expenses, payments);
  const decontari = calculeazaDecontari(solduri);

  // Pentru fiecare prieten „pe minus", ne uităm la cât de veche e datoria lui
  // (cea mai veche cheltuială în care el datorează). Dacă e mai veche de
  // NUDGE_DAYS zile, îl includem în bannerul de reamintire.
  const azi = Date.now();
  const datorniciVechi = group.members
    .map((m) => {
      const sold = solduri[m.id] || 0;
      if (sold >= -0.01) return null;
      const dateDatorii = group.expenses
        .filter((e) => e.paidBy !== m.id && getSplits(e).some((s) => s.memberId === m.id))
        .map((e) => new Date(e.createdAt).getTime());
      if (dateDatorii.length === 0) return null;
      const zile = Math.floor((azi - Math.min(...dateDatorii)) / 86400000);
      if (zile < NUDGE_DAYS) return null;
      return { m, sold, zile };
    })
    .filter(Boolean);

  // Membrul legat de contul meu (dacă m-am revendicat).
  const meMember = group.members.find((m) => m.userId === user?.user_id);
  const soldMeu = meMember ? solduri[meMember.id] || 0 : null;

  // Trimite decontarea pe WhatsApp ca text.
  const exportWhatsApp = () => {
    let mesaj = `Chit — Decontare „${group.name}"\n\n`;
    decontari.forEach((t) => {
      mesaj += `${numeMembru(t.deLa)} îi dă lui ${numeMembru(t.catre)} ${formateazaRON(t.suma)}\n`;
    });
    window.open(`https://wa.me/?text=${encodeURIComponent(mesaj)}`, "_blank");
  };

  // Trimite invitația în grup direct pe WhatsApp — doar link-ul, ca previzualizarea
  // (iconiță + text) să vină din meta tag-urile og:title/og:description ale site-ului.
  const copyInvite = () => {
    const link = `${window.location.origin}/invita/${group.id}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(link)}`, "_blank");
  };

  // Revendică un membru („Eu sunt...").
  const handleClaim = async (memberId) => {
    try {
      await joinGroup(group.id, "claim", memberId);
      toast.success("Gata! Acum îți vezi soldul tău");
    } catch {
      toast.error("Eroare");
    }
  };

  // Redenumire grup (doar proprietarul).
  const openRename = () => {
    setRenameValue(group.name);
    setRenameDialog(true);
  };
  const doRename = async () => {
    if (renameValue.trim() === "") {
      toast.error("Numele nu poate fi gol");
      return;
    }
    try {
      await renameGroup(group.id, renameValue);
      toast.success("Nume actualizat");
      setRenameDialog(false);
    } catch {
      // eroarea e deja afișată din context; păstrăm dialogul deschis
    }
  };

  // Părăsirea grupului (membru invitat).
  const doLeave = async () => {
    try {
      await leaveGroup(group.id);
      setLeaveConfirm(false);
      toast.success("Ai părăsit grupul");
      navigate("/");
    } catch {
      setLeaveConfirm(false);
    }
  };

  // Anunță un prieten (pe minus) printr-un mesaj WhatsApp.
  const notifyMember = (m, sold) => {
    const mesaj = `Salut, ${m.name}! În grupul „${group.name}" ești pe minus cu ${formateazaRON(-sold)}. Când poți, hai să fim chit :)`;
    window.open(`https://wa.me/?text=${encodeURIComponent(mesaj)}`, "_blank");
  };

  const handleAddMember = async () => {
    if (newMemberName.trim() === "") {
      toast.error("Scrie un nume");
      return;
    }
    // Gratuit: maxim 5 membri într-un grup.
    if (!isPremium && group.members.length >= 5) {
      setMemberDialog(false);
      toast.error("Grupurile cu peste 5 membri fac parte din Chit Premium.", {
        action: { label: "Vezi Premium", onClick: () => navigate("/premium") },
      });
      return;
    }
    try {
      await addMember(group.id, newMemberName);
      toast.success(`${newMemberName.trim()} a fost adăugat`);
      setNewMemberName("");
      setMemberDialog(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(detail.replace("PREMIUM:", "") || "Eroare la adăugare");
    }
  };

  const openEdit = (expense) => {
    setEditingExpense(expense);
    setExpenseDrawer(true);
  };

  const openAdd = () => {
    setEditingExpense(null);
    setExpenseDrawer(true);
  };

  return (
    <div className="min-h-screen pb-28">
      <GroupHeader
        group={group}
        total={total}
        onBack={() => navigate("/")}
        onAddMember={() => setMemberDialog(true)}
        onInvite={copyInvite}
        onRename={openRename}
        onLeaveClick={() => setLeaveConfirm(true)}
      />

      <div className="px-4 pt-4">
        <GroupSummaryCard total={total} meMember={meMember} soldMeu={soldMeu} decontariCount={decontari.length} />

        {!meMember && <ClaimPrompt members={group.members} onClaim={handleClaim} />}

        <NudgeBanner datorniciVechi={datorniciVechi} nudgeDays={NUDGE_DAYS} onNotify={notifyMember} />
      </div>

      <div className="px-4 pt-4">
        <Tabs defaultValue="cheltuieli" className="w-full">
          <TabsList className="grid grid-cols-3 w-full rounded-2xl bg-slate-100 p-1 h-12">
            <TabsTrigger value="cheltuieli" data-testid="tab-cheltuieli" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold">
              Cheltuieli
            </TabsTrigger>
            <TabsTrigger value="balante" data-testid="tab-balante" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold">
              Balanțe
            </TabsTrigger>
            <TabsTrigger value="decontare" data-testid="tab-decontare" className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold">
              Decontare
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cheltuieli" className="mt-5">
            <ExpensesTab
              group={group}
              isPremium={isPremium}
              onEdit={openEdit}
              onDeleteExpense={(id) => deleteExpense(group.id, id)}
              onDeletePayment={(id) => deletePayment(group.id, id)}
            />
          </TabsContent>

          <TabsContent value="balante" className="mt-5">
            <BalancesTab group={group} user={user} solduri={solduri} onNotify={notifyMember} />
          </TabsContent>

          <TabsContent value="decontare" className="mt-5">
            <SettlementTab
              decontari={decontari}
              numeMembru={numeMembru}
              onExportWhatsApp={exportWhatsApp}
              onMarkPaid={(t) => {
                addPayment(group.id, { from: t.deLa, to: t.catre, amount: t.suma });
                toast.success("Marcat ca plătit");
              }}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* FAB adaugă cheltuială */}
      <div className="fixed bottom-6 left-0 right-0 z-30 px-4 max-w-md mx-auto">
        <button
          data-testid="add-expense-fab"
          onClick={openAdd}
          className="w-full h-14 rounded-2xl brand-bg brand-shadow text-white font-bold text-base shadow-lg flex items-center justify-center gap-2 active:scale-95 transition-transform"
        >
          <Plus className="h-6 w-6" strokeWidth={2.5} />
          Adaugă cheltuială
        </button>
      </div>

      <AddExpenseDrawer open={expenseDrawer} onOpenChange={setExpenseDrawer} group={group} expense={editingExpense} />

      <AddMemberDialog
        open={memberDialog}
        onOpenChange={setMemberDialog}
        value={newMemberName}
        onChange={setNewMemberName}
        onSubmit={handleAddMember}
      />

      <RenameGroupDialog
        open={renameDialog}
        onOpenChange={setRenameDialog}
        value={renameValue}
        onChange={setRenameValue}
        onSubmit={doRename}
      />

      <LeaveGroupDialog
        open={leaveConfirm}
        onOpenChange={setLeaveConfirm}
        groupName={group.name}
        onConfirm={doLeave}
      />
    </div>
  );
}
